import LanguageContext from '../../../../../../../app/contexts/language';
import i18n from '../../../../../../../lib/i18n';
import { LoadChangedAction } from '../../../../enums/load-changed-action.enum';
import {
    CarrierMetadataResponse,
    ILoadHistoryEventResponse,
} from '../../../../interfaces/ILoadHistoryResponse';
import LoadHistoryUtils from '../../utils/load-history-drawer.utils';

const PLACEMENT_LABELS: Record<number, string> = {
    1: 'LoadHistory.Placement.First',
    2: 'LoadHistory.Placement.Second',
    3: 'LoadHistory.Placement.Third',
};

function getPlacementLabel(placement: number): string {
    const placementKey = PLACEMENT_LABELS[placement];
    return placementKey
        ? i18n.t(placementKey)
        : i18n.t('LoadHistory.Placement.Generic', { placement });
}

function isCarrierConfirmed(carrier: CarrierMetadataResponse): boolean {
    return carrier.confirmation === true || carrier.confirmed === true;
}

function HistoryItemWithCarriers({
    history,
}: Readonly<{
    history: ILoadHistoryEventResponse;
}>) {
    const userLanguage = LanguageContext.useLanguage();
    const carriers = Array.isArray(history.metadata?.carriers)
        ? history.metadata.carriers
        : [];
    const hasCarriers = carriers.length > 0;
    const isConfirmationEvent =
        history.eventType === LoadChangedAction.WaitingConfirmation;
    const hasMultipleCarriers = carriers.length > 1;

    const renderAcceptanceCarrier = (
        carrier: CarrierMetadataResponse,
        index: number,
    ) => {
        const key = carrier.code ?? `${history.eventType}-${index}`;
        return (
            <span key={key}>
                {LoadHistoryUtils.getCarrierLabel(carrier)}
                {` | `}
                {carrier.vehicleDescription ??
                    i18n.t('LoadHistory.VehicleDescriptionNotInformed')}
                {` - `}
                {LoadHistoryUtils.getCurrency(carrier, userLanguage.language)}
                <span className="carrier-rank">
                    {carrier.rank != null && carrier.chargeAmount != null
                        ? ` - Rank ${carrier.rank}`
                        : ''}
                </span>
            </span>
        );
    };

    const renderConfirmationRate = (
        carrier: CarrierMetadataResponse,
        showWithoutConfirmation: boolean,
        showRank: boolean,
    ) => {
        const shouldShowSemConfirmacao =
            showWithoutConfirmation && !isCarrierConfirmed(carrier);
        return (
            <>
                {carrier.chargeAmount != null &&
                    LoadHistoryUtils.getCurrency(
                        carrier,
                        userLanguage.language,
                    )}
                {shouldShowSemConfirmacao && (
                    <>
                        {` `}
                        <b className="load-history-event-rate-without-confirmation">
                            {`- ${i18n.t('LoadHistory.RoundSemConfirmacao')}`}
                        </b>
                    </>
                )}
                {showRank && (
                    <span className="carrier-rank">
                        {carrier.rank != null && carrier.chargeAmount != null
                            ? ` - Rank ${carrier.rank}`
                            : ''}
                    </span>
                )}
            </>
        );
    };

    const renderConfirmationCarriers = () => {
        const hasAnyConfirmation = carriers.some(isCarrierConfirmed);

        return carriers.map((carrier, index) => {
            const key = carrier.code ?? `${history.eventType}-${index}`;
            const placement = carrier.placement ?? index + 1;
            const placementLabel = getPlacementLabel(placement);
            const isLastCarrier = index === carriers.length - 1;
            const showRank = hasAnyConfirmation && isLastCarrier;
            const showWithoutConfirmation = !showRank;

            return (
                <span key={key}>
                    {placementLabel}
                    {` | `}
                    {LoadHistoryUtils.getCarrierLabel(carrier)}
                    {` | `}
                    {carrier.vehicleDescription ??
                        i18n.t('LoadHistory.VehicleDescriptionNotInformed')}
                    {` - `}
                    {renderConfirmationRate(
                        carrier,
                        showWithoutConfirmation,
                        showRank,
                    )}
                </span>
            );
        });
    };

    const renderSingleConfirmationCarrier = () => {
        const carrier = carriers[0];
        const key = carrier.code ?? `${history.eventType}-0`;
        const hasConfirmation = isCarrierConfirmed(carrier);

        return (
            <span key={key}>
                {LoadHistoryUtils.getCarrierLabel(carrier)}
                {` | `}
                {carrier.vehicleDescription ??
                    i18n.t('LoadHistory.VehicleDescriptionNotInformed')}
                {` - `}
                {renderConfirmationRate(
                    carrier,
                    !hasConfirmation,
                    hasConfirmation,
                )}
            </span>
        );
    };

    const renderCarriers = () => {
        if (!hasCarriers) {
            return (
                <span className="load-history-event-no-carrier">
                    {i18n.t('LoadHistory.CarrierWithoutRegisterOnSulaHub')}
                </span>
            );
        }

        if (isConfirmationEvent && hasMultipleCarriers) {
            return renderConfirmationCarriers();
        }

        if (isConfirmationEvent) {
            return renderSingleConfirmationCarrier();
        }

        return carriers.map((carrier, index) =>
            renderAcceptanceCarrier(carrier, index),
        );
    };

    return (
        <div
            className="load-history-event-container load-history-line"
            data-testid="load-history-event"
        >
            <div className="load-history-section-title">
                <h4 className="load-history-event-type">
                    {LoadHistoryUtils.getEventLabel(history)}
                </h4>
            </div>
            <span>
                {LoadHistoryUtils.getDateTimeLabel(
                    history.metadata.roundStartDate ?? history.eventDate,
                )}
            </span>
            {renderCarriers()}
        </div>
    );
}

export default HistoryItemWithCarriers;
